import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { flightsApi } from '../../api/flights.api';
import { hotelsApi } from '../../api/hotels.api';
import { bookingsApi } from '../../api/bookings.api';
import type { Booking, BookingType, Flight, Hotel } from '../../types';
import { toast } from '../../components/ui/Toast';
import { isAlreadyGone } from '../../api/client';
import { upsertByTime } from '../../utils/upsertByTime';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { Bell, BedDouble, CalendarDays, Icon, List, Plane, Ticket, Train } from '../../components/ui/Icon';
import { useBookingReminders } from '../../hooks/useBookingReminders';
import { useAiDataVersion, useAiPanel } from '../../components/ai/AiPanelContext';
import { FlightTicket } from '../../components/trips/bookings/FlightTicket';
import { StayTicket } from '../../components/trips/bookings/StayTicket';
import { ExperienceTicket } from '../../components/trips/bookings/ExperienceTicket';
import { FlightForm } from '../../components/trips/bookings/FlightForm';
import { HotelForm } from '../../components/trips/bookings/HotelForm';
import { BookingForm } from '../../components/trips/bookings/BookingForm';
import { TimelineView } from '../../components/trips/bookings/TimelineView';
import { BookingSection } from '../../components/trips/bookings/BookingSection';
import { AddWithAiButton, AddWithAiModal } from '../../components/trips/bookings/AddWithAi';
import { TicketExpensesScope } from '../../components/trips/bookings/TicketExpenses';
import { reservationGroups } from '../../utils/reservations';

type ModalKind =
  | { type: 'flight'; item?: Flight; pendingFiles?: File[] }
  | { type: 'hotel'; item?: Hotel; pendingFiles?: File[] }
  | { type: 'booking'; item?: Booking; defaultType?: BookingType; pendingFiles?: File[] };

type AddWithAiKind =
  | { kind: 'flight' }
  | { kind: 'hotel' }
  | { kind: 'booking'; defaultType: BookingType };

type DeleteTarget = { kind: 'flight' | 'hotel' | 'booking'; id: string };

export default function BookingsTab() {
  const { trip, loadDays } = useTripContext();
  const { t } = useTranslation();

  // Every booking mirrors itself into the itinerary as a linked activity card
  // (server side, bookingActivities.service). Those cards arrive inside the trip
  // context's `days`, which ItineraryTab reuses instead of refetching — so
  // without re-reading them here the plan keeps showing the pre-booking state
  // until a full page reload.
  const refreshItineraryCards = () => { void loadDays(); };

  const [flights, setFlights]   = useState<Flight[]>([]);
  const [hotels, setHotels]     = useState<Hotel[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading]   = useState(true);

  useAiPanel({ context: 'bookings', onAction: () => {} });

  const [collapsed, setCollapsed] = useState({ flights: false, hotels: false, transport: false, events: false });
  function toggleSection(k: keyof typeof collapsed) { setCollapsed(c => ({ ...c, [k]: !c[k] })); }

  const [view, setView] = useState<'sections' | 'timeline'>('sections');
  const [modal, setModal] = useState<ModalKind | null>(null);
  const [addWithAiModal, setAddWithAiModal] = useState<AddWithAiKind | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  // A round-trip ticket is reviewed one flight at a time; closing on the first
  // one silently dropped the return, which the user then re-typed by hand.
  const [unreviewedFlights, setUnreviewedFlights] = useState(0);
  const [confirmDiscardFlights, setConfirmDiscardFlights] = useState(false);

  function requestCloseFlightModal() {
    if (unreviewedFlights > 0) setConfirmDiscardFlights(true);
    else setModal(null);
  }

  function discardUnreviewedFlights() {
    setConfirmDiscardFlights(false);
    setUnreviewedFlights(0);
    setModal(null);
  }

  // ADR-0028: re-read when an accepted card changed a flight, stay or booking.
  const aiBookingsVersion = useAiDataVersion('bookings');
  useEffect(() => {
    Promise.all([
      flightsApi.list(trip.id),
      hotelsApi.list(trip.id),
      bookingsApi.list(trip.id),
    ]).then(([f, h, b]) => {
      setFlights(f);
      setHotels(h);
      setBookings(b.map(x => ({ ...x, documents: x.documents ?? [] })));
    }).catch(() => toast(t('bookings.toast.loadFailed'), 'error'))
      .finally(() => setLoading(false));
  }, [trip.id, aiBookingsVersion]);

  const { permission, requestPermission } = useBookingReminders(flights, hotels, bookings);
  const hasReminders =
    flights.some(f => (f.stops ?? []).some(s => s.reminderMinutes)) ||
    hotels.some(h => h.reminderMinutes) ||
    bookings.some(b => b.reminderMinutes && b.startTime);

  const reservations = useMemo(() => reservationGroups(flights, hotels, bookings), [flights, hotels, bookings]);

  const transportBookings = bookings.filter(b => b.type === 'TRANSPORT');
  const eventBookings     = bookings.filter(b => b.type !== 'TRANSPORT');

  /** "Add with AI" hands its files to the matching form, which extracts on open. */
  function openFormWithFiles(kind: AddWithAiKind, files: File[]) {
    if (files.length === 0) return;
    if (kind.kind === 'flight')      setModal({ type: 'flight', pendingFiles: files });
    else if (kind.kind === 'hotel')  setModal({ type: 'hotel', pendingFiles: files });
    else setModal({ type: 'booking', defaultType: kind.defaultType, pendingFiles: files });
  }

  async function confirmDeleteTarget() {
    if (!deleteTarget) return;
    const { kind, id } = deleteTarget;
    setDeleteTarget(null);
    const remove = {
      flight: { call: () => flightsApi.delete(trip.id, id),  drop: () => setFlights(f => f.filter(x => x.id !== id)),  done: 'bookings.toast.flightDeleted'  },
      hotel:  { call: () => hotelsApi.delete(trip.id, id),   drop: () => setHotels(h => h.filter(x => x.id !== id)),   done: 'bookings.toast.hotelDeleted'   },
      booking:{ call: () => bookingsApi.delete(trip.id, id), drop: () => setBookings(b => b.filter(x => x.id !== id)), done: 'bookings.toast.bookingDeleted' },
    }[kind];
    try {
      await remove.call();
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    remove.drop();
    toast(t(remove.done), 'success');
    refreshItineraryCards();
  }

  if (loading) return <LoadingScreen />;

  const totalStays = flights.length + hotels.length;

  return (
    <TicketExpensesScope trip={trip} reservations={reservations}>
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
              {trip.destination.toUpperCase()} · {totalStays} {t('bookings.bookingsCount', { count: totalStays })}
            </p>
            <h2 className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
              {t('bookings.yourTickets')}
            </h2>
          </div>
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-full p-0.5 gap-0.5 shrink-0">
            {([
              { key: 'sections', icon: List,          labelKey: 'bookings.view.sections' },
              { key: 'timeline', icon: CalendarDays,  labelKey: 'bookings.view.timeline' },
            ] as const).map(({ key, icon, labelKey }) => {
              const label = t(labelKey as Parameters<typeof t>[0]);
              return (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  title={label}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                    view === key
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
                >
                  <Icon as={icon} size={13} />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">
        {hasReminders && permission !== 'granted' && (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl mb-4 text-sm ${
            permission === 'denied'
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800'
              : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800'
          }`}>
            <Icon as={Bell} size={16} className={`shrink-0 ${permission === 'denied' ? 'text-red-500' : 'text-amber-500'}`} />
            <span className={`flex-1 text-xs ${permission === 'denied' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
              {permission === 'denied'
                ? t('bookings.notifications.blocked')
                : t('bookings.notifications.prompt')}
            </span>
            {permission === 'default' && (
              <button
                onClick={requestPermission}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors cursor-pointer"
              >
                {t('bookings.notifications.enableBtn')}
              </button>
            )}
          </div>
        )}

        {view === 'timeline' && (
          <TimelineView
            trip={trip}
            flights={flights}
            hotels={hotels}
            bookings={bookings}
            onEditFlight={f => setModal({ type: 'flight', item: f })}
            onDeleteFlight={id => setDeleteTarget({ kind: 'flight', id })}
            onEditHotel={h => setModal({ type: 'hotel', item: h })}
            onDeleteHotel={id => setDeleteTarget({ kind: 'hotel', id })}
            onEditBooking={b => setModal({ type: 'booking', item: b })}
            onDeleteBooking={id => setDeleteTarget({ kind: 'booking', id })}
          />
        )}

        {view === 'sections' && (<>
          <BookingSection
            icon={Plane} title={t('bookings.sections.flights')} count={flights.length}
            collapsed={collapsed.flights} onToggle={() => toggleSection('flights')}
            onAdd={() => setModal({ type: 'flight' })}
            extraAction={<AddWithAiButton onClick={() => setAddWithAiModal({ kind: 'flight' })} />}
            empty={t('bookings.empty.noFlights')}
          >
            {flights.map(f => (
              <FlightTicket key={f.id} flight={f} docs={f.documents}
                onEdit={() => setModal({ type: 'flight', item: f })}
                onDelete={() => setDeleteTarget({ kind: 'flight', id: f.id })}
              />
            ))}
          </BookingSection>

          <BookingSection
            icon={BedDouble} title={t('bookings.sections.hotels')} count={hotels.length}
            collapsed={collapsed.hotels} onToggle={() => toggleSection('hotels')}
            onAdd={() => setModal({ type: 'hotel' })}
            extraAction={<AddWithAiButton onClick={() => setAddWithAiModal({ kind: 'hotel' })} />}
            empty={t('bookings.empty.noHotels')}
          >
            {hotels.map(h => (
              <StayTicket key={h.id} hotel={h} docs={h.documents ?? []}
                onEdit={() => setModal({ type: 'hotel', item: h })}
                onDelete={() => setDeleteTarget({ kind: 'hotel', id: h.id })}
              />
            ))}
          </BookingSection>

          <BookingSection
            icon={Train} title={t('bookings.sections.transport')} count={transportBookings.length}
            collapsed={collapsed.transport} onToggle={() => toggleSection('transport')}
            onAdd={() => setModal({ type: 'booking', defaultType: 'TRANSPORT' })}
            extraAction={<AddWithAiButton onClick={() => setAddWithAiModal({ kind: 'booking', defaultType: 'TRANSPORT' })} />}
            empty={t('bookings.empty.noTransport')}
          >
            {transportBookings.map(b => (
              <ExperienceTicket key={b.id} booking={b} docs={b.documents}
                onEdit={() => setModal({ type: 'booking', item: b })}
                onDelete={() => setDeleteTarget({ kind: 'booking', id: b.id })}
              />
            ))}
          </BookingSection>

          <BookingSection
            icon={Ticket} title={t('bookings.sections.events')} count={eventBookings.length}
            collapsed={collapsed.events} onToggle={() => toggleSection('events')}
            onAdd={() => setModal({ type: 'booking', defaultType: 'EVENT' })}
            extraAction={<AddWithAiButton onClick={() => setAddWithAiModal({ kind: 'booking', defaultType: 'EVENT' })} />}
            empty={t('bookings.empty.noEvents')}
          >
            {eventBookings.map(b => (
              <ExperienceTicket key={b.id} booking={b} docs={b.documents}
                onEdit={() => setModal({ type: 'booking', item: b })}
                onDelete={() => setDeleteTarget({ kind: 'booking', id: b.id })}
              />
            ))}
          </BookingSection>
        </>)}

        <Modal
          open={modal?.type === 'flight'}
          onClose={requestCloseFlightModal}
          title={modal?.type === 'flight' && modal.item ? t('bookings.modal.editFlight') : t('bookings.modal.addFlight')}
        >
          {modal?.type === 'flight' && (
            <FlightForm
              tripId={trip.id}
              tripStartDate={trip.startDate}
              initial={modal.item}
              initialFiles={modal.pendingFiles}
              participants={trip.participants ?? []}
              onSaved={f => {
                setFlights(prev => upsertByTime(prev, f, x => x.departureTime));
                setModal(null);
                refreshItineraryCards();
                toast(modal.item ? t('bookings.toast.flightUpdated') : t('bookings.toast.flightAdded'), 'success');
              }}
              // A round trip saves its outbound leg without closing the form; refresh
              // now too, in case the user abandons the return leg still on screen.
              onExtraFlightCreated={f => { setFlights(prev => upsertByTime(prev, f, x => x.departureTime)); refreshItineraryCards(); }}
              onUnreviewedChange={setUnreviewedFlights}
              onCancel={requestCloseFlightModal}
            />
          )}
        </Modal>

        <Modal
          open={modal?.type === 'hotel'}
          onClose={() => setModal(null)}
          title={modal?.type === 'hotel' && modal.item ? t('bookings.modal.editHotel') : t('bookings.modal.addHotel')}
        >
          {modal?.type === 'hotel' && (
            <HotelForm
              tripId={trip.id}
              tripStartDate={trip.startDate}
              initial={modal.item}
              initialFiles={modal.pendingFiles}
              onSaved={h => {
                setHotels(prev => upsertByTime(prev, h, x => x.checkIn));
                setModal(null);
                refreshItineraryCards();
                toast(modal.item ? t('bookings.toast.hotelUpdated') : t('bookings.toast.hotelAdded'), 'success');
              }}
              onCancel={() => setModal(null)}
            />
          )}
        </Modal>

        <Modal
          open={modal?.type === 'booking'}
          onClose={() => setModal(null)}
          title={modal?.type === 'booking' && modal.item ? t('bookings.modal.editBooking') : t('bookings.modal.addBooking')}
        >
          {modal?.type === 'booking' && (
            <BookingForm
              tripId={trip.id}
              tripStartDate={trip.startDate}
              initial={modal.item}
              initialFiles={modal.pendingFiles}
              defaultType={modal.defaultType}
              onSaved={b => {
                const withDocs = { ...b, documents: b.documents ?? [] };
                setBookings(prev => upsertByTime(prev, withDocs, x => x.date));
                setModal(null);
                refreshItineraryCards();
                toast(modal.item ? t('bookings.toast.bookingUpdated') : t('bookings.toast.bookingAdded'), 'success');
              }}
              onCancel={() => setModal(null)}
            />
          )}
        </Modal>

        <ConfirmModal
          open={confirmDiscardFlights}
          title={t('bookings.form.unreviewedTitle', { count: unreviewedFlights })}
          body={t('bookings.form.unreviewedBody', { count: unreviewedFlights })}
          confirmLabel={t('bookings.form.unreviewedDiscard')}
          cancelLabel={t('bookings.form.unreviewedKeep')}
          onConfirm={discardUnreviewedFlights}
          onCancel={() => setConfirmDiscardFlights(false)}
        />

        <ConfirmModal
          open={!!deleteTarget}
          title={deleteTarget ? t(`bookings.deleteItem.${deleteTarget.kind}Title`) : ''}
          body={t('itinerary.delete.body')}
          onConfirm={confirmDeleteTarget}
          onCancel={() => setDeleteTarget(null)}
        />

        <AddWithAiModal
          open={!!addWithAiModal}
          onClose={() => setAddWithAiModal(null)}
          onFiles={files => {
            if (addWithAiModal) openFormWithFiles(addWithAiModal, files);
            setAddWithAiModal(null);
          }}
        />
      </div>
    </div>
    </TicketExpensesScope>
  );
}
